import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "./firebase";
import { orgCollection, orgDoc } from "./orgPaths";

/**
 * Log an audit event for HIPAA compliance tracking.
 * 
 * Every significant action in the app gets logged here:
 * - Authentication events (login, logout)
 * - Session events (start, stop, pause, resume)
 * - Note events (create, edit, submit, export, copy)
 * - Individual record access
 * - Audio recording events
 * - Data sync events
 * 
 * These logs are immutable (Firestore rules prevent updates/deletes).
 */
export async function logAuditEvent(eventType, details = {}) {
  try {
    const auditRef = orgCollection("auditLog");
    await addDoc(auditRef, {
      eventType,
      userId: auth.currentUser?.uid || details.userId || "system",
      userEmail: auth.currentUser?.email || null,
      timestamp: serverTimestamp(),
      details: {
        ...details,
        userAgent: navigator.userAgent,
        online: navigator.onLine,
      },
    });
  } catch (error) {
    // Don't throw — audit logging should never break the app
    // But store locally for later sync
    console.error("Audit log failed:", error);
    storeOfflineAuditEvent(eventType, details);
  }
}

/**
 * Store audit event locally when offline
 */
function storeOfflineAuditEvent(eventType, details) {
  try {
    const stored = JSON.parse(localStorage.getItem("pendingAuditEvents") || "[]");
    stored.push({
      eventType,
      userId: auth.currentUser?.uid || "unknown",
      timestamp: new Date().toISOString(),
      details,
    });
    localStorage.setItem("pendingAuditEvents", JSON.stringify(stored));
  } catch (e) {
    console.error("Failed to store offline audit event:", e);
  }
}

/**
 * Sync any pending offline audit events
 */
export async function syncPendingAuditEvents() {
  try {
    const stored = JSON.parse(localStorage.getItem("pendingAuditEvents") || "[]");
    if (stored.length === 0) return;

    const auditRef = orgCollection("auditLog");
    for (const event of stored) {
      await addDoc(auditRef, {
        ...event,
        syncedAt: serverTimestamp(),
        offline: true,
      });
    }

    localStorage.removeItem("pendingAuditEvents");
    console.log(`Synced ${stored.length} offline audit events`);
  } catch (error) {
    console.error("Failed to sync audit events:", error);
  }
}

// Predefined event types for consistency
export const AuditEvents = {
  // Auth
  AUTH_LOGIN: "auth.login",
  AUTH_LOGOUT: "auth.logout",
  AUTH_FAILED: "auth.failed",

  // Sessions
  SESSION_START: "session.start",
  SESSION_STOP: "session.stop",
  SESSION_PAUSE: "session.pause",
  SESSION_RESUME: "session.resume",

  // Recording
  RECORDING_START: "recording.start",
  RECORDING_STOP: "recording.stop",
  RECORDING_TRIGGER: "recording.trigger",
  RECORDING_UPLOAD: "recording.upload",

  // Notes
  NOTE_GENERATED: "note.generated",
  NOTE_EDITED: "note.edited",
  NOTE_SUBMITTED: "note.submitted",
  NOTE_DRAFT_SAVED: "note.draft_saved",
  NOTE_COPIED: "note.copied",
  NOTE_EXPORTED: "note.exported",

  // Individual records
  INDIVIDUAL_VIEWED: "individual.viewed",
  INDIVIDUAL_CREATED: "individual.created",

  // Sync
  SYNC_STARTED: "sync.started",
  SYNC_COMPLETED: "sync.completed",
  SYNC_FAILED: "sync.failed",

  // Audio
  AUDIO_DELETED: "audio.deleted",
  AUDIO_RETENTION_EXPIRED: "audio.retention_expired",
};
